import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import PublicSiteHeader from '@components/layout/PublicSiteHeader';
import SiteFooter from '@components/layout/SiteFooter';
import VideoSnapCard, { type ProfileVideoItem } from '@components/profile/VideoSnapCard';
import ProfilePhotoCollage, { type ProfileStill } from '@components/profile/ProfilePhotoCollage';
import PublicFamilyCollection, { type PublicFamilyBlock } from '@components/profile/PublicFamilyCollection';
import PublicProfileSideNav, {
  type PublicProfileNavId,
} from '@components/profile/PublicProfileSideNav';
import PublicSportsPerformancePanel, {
  type ContestSportStat,
  type ImprovementBlock,
  type RecentSkill,
} from '@components/profile/PublicSportsPerformancePanel';
import type { FoundationBlock } from '@components/profile/FoundationScoutPanel';
import type { SkillJourneyItem } from '@components/profile/SkillQuarterJourney';
import { brandColors } from '@config/brandTokens';
import { orgTypeLabel } from '@utils/orgTypes';
import { publicProfilePath } from '@utils/publicProfileSlug';
import { getPublicVisitorId } from '@components/profile/VideoReactionBar';

interface PlayerOrg {
  academyIdCode: string;
  name: string;
  publicSlug?: string;
  academyType?: string;
}

interface PublicPlayerProfileResponse {
  player: {
    firstName?: string;
    lastName?: string;
    primarySport?: string;
    secondarySport?: string;
    academyName?: string;
    schoolName?: string;
    publicSlug?: string;
    photoUrl?: string;
  };
  sport: string;
  availableSports?: string[];
  organizations?: PlayerOrg[];
  contestBySport?: ContestSportStat[];
  recentSkills?: RecentSkill[];
  skillJourneys?: SkillJourneyItem[];
  allSkillJourneys?: SkillJourneyItem[];
  profileVideos?: ProfileVideoItem[];
  improvementVideos?: ProfileVideoItem[];
  profileVideosBySport?: Record<string, ProfileVideoItem[]>;
  improvementVideosBySport?: Record<string, ProfileVideoItem[]>;
  photos?: ProfileStill[];
  family?: PublicFamilyBlock;
  foundation?: FoundationBlock | null;
  foundationBySport?: FoundationBlock[];
  improvement?: ImprovementBlock | null;
  reactions?: Record<string, { counts?: Record<string, number>; mine?: Record<string, boolean> }>;
}

function matchesActiveSport(sport: string | undefined, selected: string) {
  return String(sport || '').trim().toLowerCase() === String(selected || '').trim().toLowerCase();
}

function lookupSportList<T>(map: Record<string, T[]> | undefined, selected: string): T[] {
  if (!map) return [];
  if (Array.isArray(map[selected])) return map[selected];
  const key = Object.keys(map).find((item) => matchesActiveSport(item, selected));
  return key ? map[key] : [];
}

function parseView(raw: string | null): PublicProfileNavId {
  if (raw === 'family' || raw === 'sports') return raw;
  return 'player';
}

function mergeVideos(lists: ProfileVideoItem[][]) {
  const seen = new Set<string>();
  const out: ProfileVideoItem[] = [];
  lists.flat().forEach((video) => {
    const key = video.videoKey || video.videoUrl || '';
    if (!key || !video.videoUrl || seen.has(key)) return;
    seen.add(key);
    out.push(video);
  });
  return out;
}

function videosGroupedBySport(
  sports: string[],
  profile: PublicPlayerProfileResponse | null
): Array<{ sport: string; videos: ProfileVideoItem[] }> {
  const extraSports = [
    ...Object.keys(profile?.profileVideosBySport || {}),
    ...Object.keys(profile?.improvementVideosBySport || {}),
  ];
  const order: string[] = [];
  sports.concat(extraSports).forEach((sportName) => {
    if (!sportName) return;
    if (order.some((item) => matchesActiveSport(item, sportName))) return;
    order.push(sportName);
  });

  return order
    .map((sport) => ({
      sport,
      videos: mergeVideos([
        lookupSportList(profile?.profileVideosBySport, sport),
        lookupSportList(profile?.improvementVideosBySport, sport),
        (profile?.profileVideos || []).filter((item) => matchesActiveSport(item.sport, sport)),
        (profile?.improvementVideos || []).filter((item) => matchesActiveSport(item.sport, sport)),
      ]),
    }))
    .filter((group) => group.videos.length > 0);
}

const railCard = {
  border: '1px solid #e2e8f0',
  borderRadius: 1.25,
  bgcolor: '#fff',
  overflow: 'hidden',
};

const PublicPlayerProfilePage: React.FC = () => {
  const { slug = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<PublicPlayerProfileResponse | null>(null);
  const activeView = parseView(searchParams.get('view'));

  const sports = profile?.availableSports?.length
    ? profile.availableSports
    : ['Football', 'Cricket', 'Badminton'];
  const defaultSport =
    profile?.player?.primarySport && sports.some((item) => item.toLowerCase() === profile.player.primarySport.toLowerCase())
      ? profile.player.primarySport
      : (profile?.contestBySport || []).find((row) => row.submissionCount > 0)?.sport || sports[0] || 'Football';
  const selectedSport = searchParams.get('sport') || defaultSport;

  useEffect(() => {
    const load = async () => {
      if (!slug) {
        setError('Profile URL is required');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const query = new URLSearchParams({ sport: selectedSport, visitorId: getPublicVisitorId() });
        const response = await fetch(`/api/public/player-profile/${encodeURIComponent(slug)}?${query}`);
        const data = await response.json();
        if (!response.ok || !data?.success) {
          throw new Error(data?.message || 'Failed to load player profile');
        }
        setProfile(data.data);
      } catch (err: any) {
        setError(err?.message || 'Failed to load player profile');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [slug, selectedSport]);

  const player = profile?.player;
  const displayName = [player?.firstName, player?.lastName].filter(Boolean).join(' ').trim() || 'Player';
  const orgByName = new Map(
    (profile?.organizations || []).map((org) => [String(org.name || '').trim().toLowerCase(), org])
  );
  const affiliations = [
    { label: 'Academy', value: player?.academyName },
    { label: 'School', value: player?.schoolName },
  ].filter((item) => item.value);

  const videoSections = useMemo(
    () => videosGroupedBySport([player?.primarySport, player?.secondarySport, ...sports].filter(Boolean) as string[], profile),
    [player?.primarySport, player?.secondarySport, sports, profile]
  );

  const collagePhotos = useMemo(() => {
    const dedicated = (profile?.photos || []).filter((item) => item.url);
    if (dedicated.length) return dedicated;
    if (player?.photoUrl) return [{ url: player.photoUrl, alt: displayName }];
    return [];
  }, [profile?.photos, player?.photoUrl, displayName]);

  const sportChips = useMemo(() => {
    const names = new Set<string>();
    [player?.primarySport, player?.secondarySport].forEach((name) => {
      if (name) names.add(name);
    });
    (profile?.contestBySport || []).forEach((row) => {
      if (row.submissionCount > 0) names.add(row.sport);
    });
    return Array.from(names);
  }, [player?.primarySport, player?.secondarySport, profile?.contestBySport]);

  const setActiveView = (id: PublicProfileNavId) => {
    const params = new URLSearchParams(searchParams);
    if (id === 'player') params.delete('view');
    else params.set('view', id);
    setSearchParams(params, { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: brandColors.ice, display: 'flex', flexDirection: 'column' }}>
      <PublicSiteHeader />
      {loading && !profile ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 1.5, color: brandColors.slate }}>Loading scout profile...</Typography>
        </Box>
      ) : error ? (
        <Box sx={{ flex: 1, p: 4 }}>
          <Typography color="error" sx={{ fontWeight: 700 }}>{error}</Typography>
        </Box>
      ) : player ? (
        <Box
          sx={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '232px minmax(0, 1fr)' },
            gridTemplateAreas: {
              xs: '"identity" "main"',
              lg: '"identity main"',
            },
            alignItems: 'start',
            gap: { xs: 1.25, lg: 1.5 },
            px: { xs: 1.25, md: 2 },
            py: { xs: 1.25, md: 1.5 },
            minHeight: { lg: 'calc(100vh - 72px)' },
          }}
        >
          <Box sx={{ gridArea: 'identity', position: { lg: 'sticky' }, top: { lg: 80 } }}>
            <Stack spacing={1.25}>
              <Box sx={railCard}>
                <Box sx={{ bgcolor: brandColors.navy, px: 1.5, pt: 1.75, pb: 1.25 }}>
                  <Avatar
                    src={player.photoUrl || ''}
                    alt={displayName}
                    sx={{ width: 86, height: 86, bgcolor: brandColors.orange, fontWeight: 900, fontSize: 32, mb: 1.1 }}
                  >
                    {(player.firstName || displayName).slice(0, 1).toUpperCase()}
                  </Avatar>
                  <Typography sx={{ color: '#fff', fontWeight: 900, fontFamily: '"Sora", Arial, sans-serif', fontSize: 18, lineHeight: 1.2 }}>
                    {displayName}
                  </Typography>
                  {sportChips.length > 0 ? (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                      {sportChips.map((sportName) => (
                        <Chip
                          key={sportName}
                          label={sportName}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.14)',
                            color: '#fff',
                            fontWeight: 700,
                            height: 22,
                          }}
                        />
                      ))}
                    </Stack>
                  ) : null}
                </Box>
                {affiliations.length > 0 ? (
                  <Box sx={{ px: 1.5, py: 1.25 }}>
                    <Stack spacing={0.7}>
                      {affiliations.map((item) => {
                        const org = orgByName.get(String(item.value || '').trim().toLowerCase());
                        const href = org ? publicProfilePath(org.publicSlug, org.academyIdCode) : '';
                        return (
                          <Box key={`${item.label}-${item.value}`}>
                            <Typography sx={{ fontSize: 10, fontWeight: 800, color: brandColors.slate }}>{item.label}</Typography>
                            {href ? (
                              <Typography
                                component={RouterLink}
                                to={href}
                                sx={{ display: 'block', fontWeight: 800, color: brandColors.navy, textDecoration: 'none', fontSize: 13 }}
                              >
                                {item.value}
                                {org?.academyType ? ` · ${orgTypeLabel(org.academyType)}` : ''}
                              </Typography>
                            ) : (
                              <Typography sx={{ fontWeight: 800, fontSize: 13, color: brandColors.navy }}>{item.value}</Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                ) : null}
              </Box>
              <PublicProfileSideNav active={activeView} onSelect={setActiveView} />
            </Stack>
          </Box>

          <Box sx={{ gridArea: 'main', minWidth: 0 }}>
            {activeView === 'family' ? (
              <PublicFamilyCollection
                family={profile?.family}
                currentPlayerSlug={player.publicSlug || slug}
                reactions={profile?.reactions}
              />
            ) : activeView === 'sports' ? (
              <PublicSportsPerformancePanel
                player={player}
                contestBySport={profile?.contestBySport}
                foundation={profile?.foundation}
                foundationBySport={profile?.foundationBySport}
                skillJourneys={profile?.skillJourneys}
                allSkillJourneys={profile?.allSkillJourneys}
                improvement={profile?.improvement}
                recentSkills={profile?.recentSkills}
              />
            ) : (
              <Stack spacing={1.5}>
                {videoSections.length === 0 ? (
                  <Box>
                    <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontFamily: '"Sora", Arial, sans-serif', fontSize: 15, mb: 1 }}>
                      Videos
                    </Typography>
                    <Typography variant="body2" sx={{ color: brandColors.slate, fontWeight: 600 }}>
                      No published videos yet.
                    </Typography>
                  </Box>
                ) : (
                  videoSections.map((section) => (
                    <Box key={section.sport}>
                      <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontFamily: '"Sora", Arial, sans-serif', fontSize: 15, mb: 1 }}>
                        {section.sport} videos
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 148px))',
                          gap: 0.75,
                        }}
                      >
                        {section.videos.map((video, index) => (
                          <VideoSnapCard
                            key={`${video.videoKey || video.videoUrl}-${index}`}
                            video={video}
                            reaction={profile?.reactions?.[video.videoKey || '']}
                            compact={index > 1}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))
                )}
                <ProfilePhotoCollage photos={collagePhotos} playerName={displayName} />
              </Stack>
            )}
          </Box>
        </Box>
      ) : null}
      <SiteFooter />
    </Box>
  );
};

export default PublicPlayerProfilePage;
